/**
 * Подбор платья по фото клиента.
 *
 * Клиент прислал фото и спрашивает, есть ли такое платье. Запрос с этими фото уходит в группу
 * Telegram салона «Общее <город>»; сотрудники отвечают реплаем, ответ привязывается к запросу.
 * `PodborList` — запросы с фото и ответами (вкладка «Подбор» карточки сделки и лида).
 * `ChatPodbor` — те же запросы полосой над полем ответа «Чатов»: какие внутренние запросы по
 * этому диалогу созданы и что ответил салон; фото из ответа одной кнопкой идёт в ответ клиенту.
 * Запрос, ушедший в салон, — задача: «Активно», пока сотрудник не проверит и не нажмёт «Завершить».
 * Спека `docs/specs/2026-10-07-chat-podbor-design.md`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, ChevronRight, Forward, Paperclip, RotateCcw, Shirt, Store } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { plural } from "@/lib/plural";
import { type Podbor, type PodborAnswer, cancelPodbor, loadChatPodbor, sendPodbor, setPodborDone } from "@/lib/podbor";

const when = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)} ${iso.slice(11, 16)}`;
const line = "border-[color-mix(in_srgb,currentColor_14%,transparent)]";

const TONE: Record<Podbor["state"], string> = {
  new: "bg-[#f5a623]/15 text-[#b26a00]",
  failed: "bg-[#e53935]/15 text-[#e53935]",
  sending: "bg-[#3390ec]/15 text-[#3390ec]",
  sent: "bg-[#3390ec]/15 text-[#3390ec]",
  answered: "bg-[#2e9e4f]/15 text-[#2e9e4f]",
  closed: "bg-[color-mix(in_srgb,currentColor_10%,transparent)] opacity-70",
  cancelled: "bg-[color-mix(in_srgb,currentColor_10%,transparent)] opacity-70",
};

export function PodborState({ p }: { p: Podbor }) {
  return <span className={`whitespace-nowrap rounded-full px-2 py-px text-[12px] ${TONE[p.state]}`}>{p.state_name}</span>;
}

/** Статус задачи: «Активно» — в салон ушло и сотрудник ещё не отметил; «Завершено» — кем и когда. */
export function PodborTask({ p }: { p: Podbor }) {
  if (!p.task) return null;
  const done = p.task === "done";
  return (
    <span
      title={done ? `Завершил${p.done_user ? `: ${p.done_user}` : ""}${p.date_done ? ` · ${when(p.date_done)}` : ""}` : "Задача в работе: сотрудник ещё не отметил её завершённой"}
      className={`whitespace-nowrap rounded-full px-2 py-px text-[12px] font-medium ${done ? "bg-[color-mix(in_srgb,currentColor_10%,transparent)] opacity-70" : "bg-[#f5a623]/15 text-[#b26a00]"}`}
      data-annot="статус задачи"
    >
      {p.task_name}
    </span>
  );
}

function Photo({ src, big = false }: { src: string; big?: boolean }) {
  const size = big ? "h-[120px] w-[96px]" : "h-[72px] w-[58px]";
  return (
    <a href={src} target="_blank" rel="noreferrer" title="Открыть фото">
      <img src={`${src}${src.includes("/photo/") ? "?thumb=1" : ""}`} alt="" loading="lazy" className={`${size} rounded-lg bg-black/10 object-cover`} />
    </a>
  );
}

/** Ответ сотрудника салона из группы; `onAttach` — положить его фото в ответ клиенту. */
function Answer({ a, onAttach }: { a: PodborAnswer; onAttach?: ((file: File) => void) | undefined }) {
  const attach = useMutation({
    mutationFn: async () => {
      const r = await fetch(a.photo!, { credentials: "same-origin" });
      if (!r.ok) throw new Error(`Фото не открылось: ${r.status}`);
      return new File([await r.blob()], `podbor-${a.id}.jpg`, { type: "image/jpeg" });
    },
    onSuccess: (file) => onAttach?.(file),
  });
  return (
    // Ответ на ответ коллеги — со сдвигом под ним; глубже четырёх уровней не сдвигаем: полоса узкая.
    <li
      className={`flex gap-2 ${a.depth ? `border-l-2 pl-2 ${line}` : ""}`}
      style={a.depth ? { marginLeft: (Math.min(a.depth, 4) - 1) * 14 + 6 } : undefined}
      data-annot="ответ салона на подбор"
    >
      {a.photo && (
        <div className="flex flex-none flex-col items-center gap-0.5">
          <Photo src={a.photo} big />
          {onAttach && (
            <button
              type="button"
              disabled={attach.isPending}
              onClick={() => attach.mutate()}
              title="Приложить это фото к ответу клиенту"
              className="flex items-center gap-0.5 text-[12px] text-[#3390ec] disabled:opacity-50"
            >
              <Paperclip size={12} /> {attach.isPending ? "…" : "в ответ"}
            </button>
          )}
          {attach.isError && <span className="max-w-[96px] text-[11px] text-[#e53935]">{attach.error.message}</span>}
        </div>
      )}
      <div className="min-w-0">
        <div className="text-[12px] opacity-60">
          {a.author} · {when(a.date)}
          {a.reply_author && <span title="Ответ на ответ коллеги"> · ↳ {a.reply_author}</span>}
        </div>
        {a.text && <div className="whitespace-pre-wrap break-words">{a.text}</div>}
      </div>
    </li>
  );
}

/** Запрос целиком: что и куда отправили, фото клиента, ответы салона. */
export function PodborBody({ p, onAttach }: { p: Podbor; onAttach?: ((file: File) => void) | undefined }) {
  return (
    <div className="flex flex-col gap-1.5">
      {p.kind === "forward" && p.text && (
        <div className={`whitespace-pre-wrap break-words border-l-2 pl-2 ${line}`} data-annot="пересланное сообщение">
          {p.text}
        </div>
      )}
      {p.question && (
        <div className="whitespace-pre-wrap break-words">
          {p.kind === "forward" ? "Комментарий" : "Вопрос салону"}: {p.question}
        </div>
      )}
      {p.error && <div className="text-[#e53935]">Не ушёл: {p.error}</div>}
      <div className="flex flex-wrap gap-1" data-annot="фото клиента на подбор">
        {p.photos.map((src) => (
          <Photo key={src} src={src} />
        ))}
      </div>
      {p.answers.length > 0 ? (
        <ul className={`flex flex-col gap-2 border-t pt-1.5 ${line}`}>
          {p.answers.map((a) => (
            <Answer key={a.id} a={a} onAttach={onAttach} />
          ))}
        </ul>
      ) : (
        p.state === "sent" && <div className="opacity-60">Салон пока не ответил — ответ реплаем в группе появится здесь сам.</div>
      )}
    </div>
  );
}

const head = (p: Podbor) =>
  [
    p.group ? `«${p.group}»` : p.city,
    p.count > 1 ? `${p.count} ${plural(p.count, "сообщение", "сообщения", "сообщений")}` : null,
    p.kind !== "podbor" && !p.photos.length ? null : `${p.photos.length} фото`,
    when(p.date),
    p.user || (p.ai ? "AI" : ""),
  ]
    .filter(Boolean)
    .join(" · ");

/** Вкладка «Подбор» карточки сделки и лида: все запросы с фото и ответами. */
export function PodborList({ rows }: { rows: Podbor[] }) {
  if (!rows.length) {
    return (
      <div className="text-[12.5px] text-fg-muted">
        На подбор по этому клиенту ничего не отправляли. Запрос появляется, когда фото клиента из «Чатов» уходит в группу салона.
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2 text-[13px]">
      {rows.map((p) => (
        <section key={p.id} className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3" data-annot="запрос на подбор">
          <div className="mb-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-medium">{p.kind === "forward" ? "Переслано в салон" : p.type_name} · запрос {p.id}</span>
            <PodborTask p={p} />
            <PodborState p={p} />
            <span className="text-fg-muted">{head(p)}</span>
            {p.task === "done" && (
              <span className="text-fg-muted">
                завершил{p.done_user ? ` ${p.done_user}` : ""}
                {p.date_done ? ` · ${when(p.date_done)}` : ""}
              </span>
            )}
            <Link className="underline" to={`/chats/${p.chat}`}>
              чат
            </Link>
          </div>
          <PodborBody p={p} />
        </section>
      ))}
    </div>
  );
}

/** Полоса над полем ответа «Чатов»: запросы на подбор этого диалога. */
export function ChatPodbor({ chatId, onAttach }: { chatId: number; onAttach: (file: File) => void }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState<Record<number, boolean>>({});
  const [showDone, setShowDone] = useState(false);
  const q = useQuery({
    queryKey: ["chat-podbor", chatId],
    queryFn: () => loadChatPodbor(chatId),
    // Ответ салона приходит реплаем в группе в любой момент — подтягиваем, не перезагружая чат.
    refetchInterval: 30_000,
  });
  const done = () => {
    void qc.invalidateQueries({ queryKey: ["chat-podbor", chatId] });
    void qc.invalidateQueries({ queryKey: ["chat-suggest", chatId] });
    void qc.invalidateQueries({ queryKey: ["client-chat", chatId] });
    void qc.invalidateQueries({ queryKey: ["chat-actions", chatId] });
    void qc.invalidateQueries({ queryKey: ["client-chats"] }); // число на отборе «Задачи» и значок в списке
  };
  const send = useMutation({ mutationFn: (id: number) => sendPodbor(chatId, id), onSettled: done });
  const cancel = useMutation({ mutationFn: (id: number) => cancelPodbor(chatId, id), onSettled: done });
  const task = useMutation({ mutationFn: (v: { id: number; done: boolean }) => setPodborDone(chatId, v.id, v.done), onSettled: done });
  // Задача висит в полосе, пока сотрудник не отметит её завершённой; завершённые свёрнуты в строку
  // под полосами и остаются на вкладке «Подбор» сделки.
  const all = q.data?.rows ?? [];
  const finished = all.filter((p) => p.task === "done");
  const rows = showDone ? all : all.filter((p) => p.task !== "done");
  if (!all.length) return null;
  const busy = send.isPending || cancel.isPending || task.isPending;
  const error = send.error ?? cancel.error ?? task.error;
  return (
    // Запросов и ответов бывает много: полосы не выше трети экрана, дальше — прокрутка; лента чата остаётся видна.
    <div className="mb-2 flex max-h-[32vh] flex-none flex-col gap-1 overflow-y-auto" data-annot="подбор по фото">
      {rows.map((p) => {
        // Развёрнуты только те, что ждут отправки: ответ салона виден в шапке («салон ответил · ответов N»)
        // и раскрывается нажатием — иначе полосы с карточкой AI вытесняют ленту сообщений.
        const shown = open[p.id] ?? (p.state === "new" || p.state === "failed");
        return (
          <div key={p.id} className="rounded-[14px] border border-[var(--tg-sec)]/25 bg-[var(--tg-surface)] px-3 py-1.5 text-[13.5px] shadow-[var(--tg-shadow)]" data-annot="задача в салон">
            <div className="flex items-start gap-2">
              <button
                type="button"
                onClick={() => setOpen((had) => ({ ...had, [p.id]: !shown }))}
                className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5 text-left"
                title={
                  p.kind === "forward"
                    ? "Сообщение клиента переслано в группу салона"
                    : p.kind === "question"
                      ? "Вопрос по заказу в группу салона; ответы реплаем ложатся комментариями в сделку CRM"
                      : `Запрос в группу салона: ${p.type_name.toLowerCase()}`
                }
              >
                {shown ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                <span className="flex items-center gap-1 font-medium">
                  {p.kind === "forward" ? <Forward size={14} /> : p.kind === "question" ? <Store size={14} /> : <Shirt size={14} />}{" "}
                  {p.kind === "forward" ? "В салон" : p.kind === "question" ? "Вопрос салону" : p.type === "podbor" ? "Подбор" : p.type_name}
                </span>
                <PodborTask p={p} />
                <PodborState p={p} />
                <span className="min-w-0 truncate text-[var(--tg-sec)]">{head(p)}</span>
                {p.answers.length > 0 && <span className="text-[var(--tg-sec)]">ответов {p.answers.length}</span>}
              </button>
              {/* Сотрудник проверил, что пересланное в салон сделано, — задача завершена; рано — вернуть. */}
              {p.task && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => task.mutate({ id: p.id, done: p.task === "active" })}
                  title={p.task === "active" ? "Проверили: пересланное в салон сделано — задача завершена" : "Завершили рано — вернуть задачу в активные"}
                  className={`flex flex-none items-center gap-1 whitespace-nowrap rounded-full px-2.5 text-[13px] font-medium leading-[24px] disabled:opacity-50 ${
                    p.task === "active" ? "bg-[var(--tg-primary)] text-white" : "text-[var(--tg-sec)] hover:bg-[var(--tg-hover)]"
                  }`}
                  data-annot={p.task === "active" ? "завершить задачу" : "вернуть задачу"}
                >
                  {p.task === "active" ? <Check size={14} /> : <RotateCcw size={13} />}
                  {p.task === "active" ? "Завершить" : "Вернуть"}
                </button>
              )}
            </div>
            {shown && (
              <div className="mt-1.5 max-h-[260px] overflow-y-auto">
                <PodborBody p={p} onAttach={onAttach} />
                {(p.state === "new" || p.state === "failed") && (
                  <div className="mt-2 flex flex-wrap items-center gap-1 text-[14px]">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => send.mutate(p.id)}
                      title="Фото клиента и вопрос уйдут в группу салона в Telegram"
                      className="rounded-full bg-[var(--tg-primary)] px-3.5 py-1 font-medium text-white disabled:opacity-50"
                    >
                      {send.isPending ? "Отправляю…" : `Отправить в ${p.group ? `«${p.group}»` : "группу салона"}`}
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => cancel.mutate(p.id)}
                      className="rounded-full px-3 py-1 font-medium text-[var(--tg-sec)] hover:bg-[var(--tg-hover)] disabled:opacity-50"
                    >
                      Не отправлять
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
      {finished.length > 0 && (
        <button
          type="button"
          onClick={() => setShowDone(!showDone)}
          className="self-start px-3 text-[12.5px] text-[var(--tg-sec)] hover:text-[var(--tg-text)]"
          data-annot="завершённые задачи"
        >
          {showDone ? "Скрыть завершённые" : `Завершённые задачи: ${finished.length}`}
        </button>
      )}
      {error && <p className="px-3 text-[13px] text-[#e53935]">{error.message}</p>}
    </div>
  );
}
